/* global browser */

import { create, uuid } from '../utils.mjs';
import Loader from '../loader.mjs';
import EVENTS from '../events.mjs';
import OAuth from './oauth.mjs';
import { I18n } from '../helpers/i18n.mjs';
import { DEFAULT_THEME } from '../helpers/color.mjs';
import { IML } from '../deps/iml.mjs';
import EventManager from '../eventManager.mjs';

export default class RemoteFormPanel extends EventTarget {
	constructor(relative, parentForm = null) {
		super();

		this.panel = null;
		this.relative = relative;
		this.parentForm = parentForm;
	}

	async build(instructions, metadata = {}, allowApiCall = false) {
		if (!this.panel) {
			this.panel = create('imt-panel.remote');
			this.panel.width = 450;
			this.panel.position = ['right', 'left'];
			this.panel.relative = this.relative;
			this.panel.parentForm = this.parentForm;
			this.panel.spacing = 8;

			this.panel.addEventListener(EVENTS.PANEL.CLOSE, (event) => EventManager.dispatch(this, EVENTS.PANEL.CLOSE));
		} else {
			// Clear content when rebuilding
			this.panel.header.innerHTML = '';
			this.panel.body.innerHTML = '';
			this.panel.footer.innerHTML = '';
		}

		if (instructions?.src && /^api:\/\/imt-forms\/(.*)$/.test(instructions.src)) {
			this.panel.allowApiCall = true;
		}

		const parentFieldset = this.relative?.closest('[field]')?.fieldset;
		this.panel.allowApiCall = allowApiCall || this.relative?.closest('[field]')?.allowApiCall;

		if (parentFieldset?.rpcData) {
			metadata.data = { ...metadata.data, ...parentFieldset.form.defaultRpcData, ...parentFieldset.rpcData };
		}

		// Header

		this.panel.header.append(create(`h1 ${instructions.name}`));

		const parentForm = this.relative?.closest('imt-forman') || this.parentForm;
		const theme = instructions.theme || parentForm?.meta?.module?.package?.theme || DEFAULT_THEME;

		if (theme) {
			const color = (theme.toHex ? theme.toHex() : theme).toLowerCase();

			if (/^#[0-9A-F]{6}$/i.test(color)) {
				this.panel.header.classList.add('gradient', `theme-${color.replace('#', '')}`);
			}
		}

		const headerButtons = create('div.header-buttons');
		const menu = create('button.panel-menu[type="button"]');

		menu.append(create('i.fas.fa-ellipsis-v'));
		menu.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();
			const relativeForm = this.relative.closest('imt-forman');

			if (relativeForm) {
				EventManager.dispatch(relativeForm, EVENTS.PANEL.OPEN_MENU, {
					detail: {
						relative: menu,
					},
					cancelable: true,
				});
			}
		});
		headerButtons.append(menu);

		if ('undefined' !== typeof sim && instructions.help) {
			const helpBtn = create('button.close[type="button"]');

			helpBtn.append(create('i.fas.fa-question'));
			helpBtn.addEventListener('click', (event) => {
				event.preventDefault();
				event.stopPropagation();

				window.sim(window).openHelp(instructions.help);
			});
			headerButtons.append(helpBtn);
		}

		const close = create('button.close[type="button"]');

		close.append(create('i.far.fa-times'));
		close.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();

			this.panel.close();
		});
		headerButtons.append(close);

		this.panel.header.append(headerButtons);

		let config;

		try {
			config = !instructions.src
				? instructions
				: await Loader.guardedLoad(instructions.src, metadata, this.panel.allowApiCall);
		} catch (error) {
			this.panel.warn(error);
			console.error(error);
			return;
		}

		// Form

		this.form = create('imt-forman');

		const meta = parentForm?.meta || {};

		this.form.meta = { ...{}, ...meta, ...metadata, parentFieldset };
		if (this.form.meta.imlContext && meta.imlContext) {
			this.form.meta.imlContext = { ...this.form.meta.imlContext, ...meta.imlContext };
		}

		this.form.options = parentForm?.options || {};

		this.form.values.load(config.values);

		if (parentForm?.hasAttribute('debug')) {
			this.form.setAttribute('debug', '');
		}

		// Body

		this.panel.body.append(this.form);

		// Footer

		const [display, buttons] = this.panel.footerControls;
		const id = uuid();
		const advanced = create(`imt-switch.xs#${id}`);
		const label = create(`label[for="${id}"].show-advanced-label`);

		display.setAttribute('role', 'button');
		display.setAttribute('tabindex', '0');
		display.classList.add('advanced-toggle-container');

		const triggerAdvancedToggle = (event) => {
			if (!advanced.isConnected) return;

			// enter / space
			if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;

			// ignore clicks on the switch itself
			if (event.target.closest('imt-switch')) return;

			event.preventDefault();
			advanced.toggle();
		};

		display.addEventListener('click', triggerAdvancedToggle);
		display.addEventListener('keydown', triggerAdvancedToggle);

		label.textContent = I18n.l('common.showAdvanced');
		advanced.addEventListener('input', () => (this.form.advancedParametersVisible = advanced.active));

		this.form.addEventListener(EVENTS.FORM.ADVANCED_PARAMETERS_CHANGE, (event) => {
			if (event.detail) {
				display.append(advanced);
				display.append(label);
			} else {
				advanced.remove();
				label.remove();
				if (this.form.advancedParametersVisible) {
					advanced.toggle();
				}
			}
		});

		await this.form.build(config.config);

		if (config.buttons) {
			for (const buttonConfig of config.buttons) {
				if (buttonConfig.action === 'close') {
					this._generateCloseButton(buttonConfig.label, buttons);
					continue;
				}

				let button;
				const label = I18n.l(buttonConfig.label);

				// Override buttons for specific vendors in case of OAuth flows
				if (buttonConfig.html) {
					button = create('div.custom-btn-container');
					button.innerHTML = IML.execute(IML.parse(buttonConfig.html), { label });
				} else {
					button = create('button.btn.btn-sm.btn-primary[type="button"]');
					button.textContent = label;
				}

				switch (buttonConfig.action) {
					case 'submit': {
						button.addEventListener('click', async (event) => {
							event.preventDefault();

							if (!this.form.validate()) return;

							if (
								EventManager.dispatch(this, EVENTS.FORM.SUBMITTED, {
									detail: {
										get value() {
											return this.form.value;
										},
										panel: this.panel,
										form: this.form,
									},
									cancelable: true,
								})
							) {
								this.panel.close();
							}
						});

						break;
					}

					case 'http': {
						let oauthWindow;

						button.addEventListener('click', async (event) => {
							event.preventDefault();

							if (!this.form.validate()) return;

							this.panel.setLoading(true);

							const accountType = this.form.querySelector('imt-input-select[name="accountType"] imt-option[selected]');

							if (!browser.mobile && accountType?.getAttribute('data-method') === 'oauth') {
								oauthWindow = OAuth.win();

								if (!oauthWindow) {
									button.removeAttribute('disabled');
									this.panel.warn(new Error(I18n.l('common.popup')));
								}
							}
							// on desktops, pre-open the window here because we're in click event handler so popup is not blocked
							// on mobiles, a special prompt is showed to the user later in formanAuth process

							const path = 'api://' + buttonConfig.address.replace(/^\//, '');

							const response = await (['GET'].includes(buttonConfig.method)
								? Loader.guardedLoad(path, {}, this.panel.allowApiCall)
								: this.form.save({
										method: buttonConfig.method,
										action: path,
										multipart: config.multipart || false,
								  }));

							if (!response) {
								oauthWindow?.close();
								this.panel.setLoading(false);
								return;
							}

							if (response.formula) {
								if (!response.formula.oauth) oauthWindow?.close();

								if (response.formula.oauth) {
									try {
										await OAuth.auth.call(
											this.panel,
											oauthWindow,
											response.formula.oauth.account,
											instructions.options?.oauth?.scope
												?.filter((s) => s.type === response.formula.oauth.provider)
												.flatMap((s) => s.scope),
											this.form?.meta?.imlContext,
										);

										EventManager.dispatch(this, EVENTS.FORM.SUBMITTED, {
											detail: {
												data: [
													response.connection.id,
													response.connection.name,
													response.connection.accountName,
													response.connection.metadata,
												],
											},
										});

										this.panel.close();
									} catch (ex) {
										button.removeAttribute('disabled');

										return this.panel.warn(ex);
									}
								} else if (response.formula.rebuild) {
									await this.build(
										Object.assign(instructions, {
											src: response.formula.rebuild.url, // Pass context to replace path parameter (e.g. device)
										}),
									);

									this.panel.setLoading(false);
								} else if (response.formula.success) {
									EventManager.dispatch(this, EVENTS.FORM.SUBMITTED, {
										detail: { data: response.formula.success },
									});

									this.panel.close();
								}
							} else {
								EventManager.dispatch(this, EVENTS.FORM.SUBMITTED, {
									detail: { data: response },
								});

								this.panel.close();
							}
						});

						break;
					}

					case 'handler':
						button.addEventListener('click', buttonConfig.handler);

						break;
				}

				buttons.append(button);
			}
		}
	}

	open() {
		if (this.panel) this.panel.open();
	}

	_generateCloseButton(label, buttons) {
		const button = create('button.btn.btn-sm.btn-outline-secondary[type="button"]');

		button.textContent = I18n.l(label);
		button.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();
			this.panel.close();
		});

		buttons.append(button);
	}
}
